import React, { useState, useEffect } from 'react';
import StatusBadge from '../status-badge/statusBadge';
import utils from '../../utils/product/product-utils';
import { deleteUser, saveModificationsUser, getCommercialInfo, updateCommercialInfo } from '../../utils/users/users';
import './userListComponent.css';
import Slider from '../slider/slider';
import SELLERS from '../../config/sellers';

const UserList = (props) => {
  const [isAuthorized, setIsAuthorized] = useState(!!props.userData.active);
  const [isAdmin, setIsAdmin] = useState(!!props.userData.tags.includes('admin'));
  const [salesAgentInput, setSalesAgentInput] = useState(props.userData.salesAgent);
  const [errorSalesAgent, setErrorSalesAgent] = useState(null);
  const [internalIdInput, setInternalIdInput] = useState(props.userData.internalId);
  const [errorInternalId, setErrorInternalId] = useState(null);
  const [error, setError] = useState(null);

  // Commercial info state
  const [replicaC1, setReplicaC1] = useState(100);
  const [replicaC2, setReplicaC2] = useState(0);
  const [commercialSalesAgent, setCommercialSalesAgent] = useState('');
  const [commercialInfoLoaded, setCommercialInfoLoaded] = useState(false);
  const [errorCommercialInfo, setErrorCommercialInfo] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!isExpanded || commercialInfoLoaded) return;
    const loadCommercialInfo = async () => {
      try {
        const response = await getCommercialInfo(props.userData.internalId);
        if (response.success && response.data) {
          setReplicaC1(response.data.replica_C1 !== undefined ? response.data.replica_C1 : 100);
          setReplicaC2(response.data.replica_C2 || 0);
          setCommercialSalesAgent(response.data.salesAgent || '');
        }
        setCommercialInfoLoaded(true);
      } catch (err) {
        console.error('Error loading commercial info:', err);
        setCommercialInfoLoaded(true);
      }
    };
    if (props.userData.internalId) {
      loadCommercialInfo();
    }
  }, [isExpanded, commercialInfoLoaded, props.userData.internalId]);

  const getStatusBadge = () => {
    const badges = [];

    if (!props.userData.active) {
      badges.push(<StatusBadge key={`${props.userData._id}-inactive`} status="INACTIVO" badgeStyle="secondary" />);
    } else {
      badges.push(<StatusBadge key={`${props.userData._id}-active`} status="ACTIVO" badgeStyle="primary" />);
    }

    // Admin case
    if (props.userData.tags.includes('admin')) {
      badges.push(<StatusBadge key={`${props.userData._id}-admin`} status="ADMIN" badgeStyle="danger" />);
    }

    return badges;
  };

  const saveChanges = async (id) => {
    const c1 = Number(replicaC1) || 0;
    const c2 = Number(replicaC2) || 0;
    if (c1 + c2 !== 100) {
      setErrorCommercialInfo('La suma de Replica C1 y Replica C2 debe ser 100.');
      return;
    }

    if (!errorInternalId && !errorSalesAgent && salesAgentInput && internalIdInput) {
      const tags = [];
      if (isAdmin) {
        tags.push('admin');
      }

      const data = {
        id,
        tags,
        active: isAuthorized,
        internalId: internalIdInput,
        salesAgent: salesAgentInput
      };

      try {
        // Save user data
        const userRes = await saveModificationsUser(data);

        if (!userRes.success) {
          setError('Ocurrio un error guardando los datos del usuario.');
          return;
        }

        // Save commercial info if it was loaded
        if (commercialInfoLoaded && internalIdInput) {
          const commercialData = {
            internalId: internalIdInput,
            replica_C1: parseInt(replicaC1, 10) || 0,
            replica_C2: parseInt(replicaC2, 10) || 0,
            salesAgent: commercialSalesAgent || '',
          };

          const commercialRes = await updateCommercialInfo(commercialData);

          if (!commercialRes.success) {
            setErrorCommercialInfo('Error guardando información comercial.');
            return;
          }
        }

        window.location.reload();
      } catch (err) {
        setError('Ocurrio un error guardando los datos.');
      }
    } else {
      setError('No puede guardarse porque hay campos faltantes.');
    }
  };

  const deleteUserHandler = (id) => {
    const confirm = window.confirm('¿Esta seguro que desea eliminar el usuario?');
    if (confirm) {
      // Delete the user
      deleteUser(id).then((res) => {
        if (res.success) {
          window.location.reload();
        } else {
          setError('Ocurrio un error eliminando el usuario.');
        }
      });
    }
  };

  const handleSalesAgent = (e) => {
    const value = e.target.value;
    if (value) {
      setErrorSalesAgent(null);
      setSalesAgentInput(value);
    } else {
      setErrorSalesAgent('El agente de ventas no puede estar vacio.');
    }
  };

  const handleInternalId = (e) => {
    const value = e.target.value;
    if (value) {
      setErrorInternalId(null);
      setInternalIdInput(value);
    } else {
      setErrorInternalId('El numero de cuenta no puede estar vacio.');
    }
  };

  const handleSetAdmin = (e) => {
    const value = e.target.value;
    if (value) {
      setIsAdmin(!isAdmin);
    }
  };

  const handleSetActive = (e) => {
    const value = e.target.value;
    if (value) {
      setIsAuthorized(!isAuthorized);
    }
  };

  const handleReplicaC1 = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= 100) {
      setReplicaC1(value);
      setReplicaC2(100 - value);
      setErrorCommercialInfo(null);
    } else if (e.target.value === '') {
      setReplicaC1(0);
      setReplicaC2(100);
    }
  };

  const handleReplicaC2 = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= 100) {
      setReplicaC2(value);
      setReplicaC1(100 - value);
      setErrorCommercialInfo(null);
    } else if (e.target.value === '') {
      setReplicaC2(0);
      setReplicaC1(100);
    }
  };

  const handleCommercialSalesAgent = (e) => {
    setCommercialSalesAgent(e.target.value);
  };

  const hasValidReplicaSplit = () => {
    const c1 = Number(replicaC1) || 0;
    const c2 = Number(replicaC2) || 0;
    return c1 + c2 === 100;
  };

  return (
    <div className="card card-custom-history" key="1">
      <div className="card-header card-header-history" id="headingOne">
        <h5 className="mb-0">
          <button className="btn btn-width-100"
            data-toggle="collapse" data-target={`#collapse-${props.userData._id}`}
            aria-expanded="true" aria-controls="collapseOne"
            onClick={() => setIsExpanded(true)}>
            <table className="table table-custom-history">
              <tbody>
                <tr className="media media-custom">
                  <td className="media-left media-left-custom">
                    <span>
                      {getStatusBadge()}
                    </span>
                  </td>
                  <td className="media-body media-body-custom">
                    <h5 className="media-heading font-yellow text-bold hidden-xs">
                      {props.userData.fullName}
                    </h5>
                    <span><strong>Email: </strong>
                      {props.userData.email}
                    </span>
                    <br />
                    <span><strong>CUIT: </strong>
                      {props.userData.taxIdentifier}
                    </span>
                    <br />
                    <span><strong>Domicilio: </strong>
                      {props.userData.address}
                    </span>
                    <br />
                    {props.userData.city && props.userData.province &&
                      <>
                        <span><strong>Localidad/Provincia: </strong>
                          {props.userData.city + '-' + props.userData.province}
                        </span>
                        <br />
                      </>}
                    <span><strong>Fecha Alta: </strong>
                      {utils.formatDate(props.userData.createdAt)}
                    </span>
                    <br />
                    <span><strong>Telefono: </strong>
                      {props.userData.phone}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </button>
        </h5>
      </div>
      <div id={`collapse-${props.userData._id}`}
        className="collapse collapse-custom" aria-labelledby="headingOne" data-parent="#accordion">
        <div className="card-body">
          <Slider title="Activo" checked={isAuthorized} setChecked={handleSetActive} />
          <Slider title="Admin" checked={isAdmin} setChecked={handleSetAdmin} />
          <div className="input-group mb-3 col-12 col-md-8 input-group-users">
            <label className="label-inputs">
              Mail Responsable
              <input type="text"
                className="form-control" aria-describedby="basic-addon3"
                onChange={handleSalesAgent}
                value={salesAgentInput}
                onBlur={handleSalesAgent}
              />
            </label>
            <p className="error-login">{errorSalesAgent}</p>
            <label className="label-inputs">
              Nro Cuenta
              <input type="text"
                className="form-control" aria-describedby="basic-addon3"
                onChange={handleInternalId}
                value={internalIdInput}
                onBlur={handleInternalId}
              />
            </label>
            <p className="error-login">{errorInternalId}</p>
          </div>
          <hr />
          <h6 className="mb-3">Información Comercial</h6>
          <div className="input-group mb-3 col-12 col-md-8 input-group-users">
            <label className="label-inputs">
              Replica C1 (%)
              <input type="number"
                className="form-control"
                min="0"
                max="100"
                onChange={handleReplicaC1}
                value={replicaC1}
              />
            </label>
            <label className="label-inputs">
              Replica C2 (%)
              <input type="number"
                className="form-control"
                min="0"
                max="100"
                onChange={handleReplicaC2}
                value={replicaC2}
              />
            </label>
            <label className="label-inputs">
              Agente Comercial
              <select
                className="form-control"
                onChange={handleCommercialSalesAgent}
                value={commercialSalesAgent}
              >
                <option value="">-- Seleccionar --</option>
                {SELLERS.map((seller) => (
                  <option key={seller.email} value={seller.email}>
                    {seller.name} ({seller.email})
                  </option>
                ))}
              </select>
            </label>
            <p className="error-login">{errorCommercialInfo}</p>
          </div>
          <div className="mb-3 col-12 col-md-8">
            <button
              className="btn btn-dark"
              onClick={() => saveChanges(props.userData._id)}
              disabled={!hasValidReplicaSplit()}
            >
              <i className="material-icons icon-action icon-buttons-user">save</i>
              Guardar
            </button>
            <button className="btn btn-dark" style={{ marginLeft: '10px' }} onClick={() => deleteUserHandler(props.userData._id)}>
              <i className="material-icons icon-action icon-buttons-user">delete</i>
              Eliminar
            </button>
            {!hasValidReplicaSplit() && (
              <p className="error-login">La suma de Replica C1 y Replica C2 debe ser 100.</p>
            )}
            <p className="error-login">{error}</p>
          </div>
        </div>
      </div>
    </div>
  )
};

export default UserList;
